import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import wordListJson from 'src/assets/word_list.json';

export class PasswordValidators {
  public static readonly MIN_LENGTH = 12;
  public static readonly MAX_REP = 2;
  public static readonly MAX_SEQ = 3;
  public static readonly MAX_KEY_CHAIN = 3;

  /**
   * Checks if password contain dictionary words
   */
  public static dictionary: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const value: string | undefined = control.value?.toLowerCase();
    if (value) {
      for (const word of wordListJson.words) {
        if (value.includes(word)) return { passwdDict: word };
      }
    }
    return null;
  };

  /**
   * Checks if password contain sequence on following keyboard chars
   */
  public static keyChain(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value: string | undefined = control.value?.toLowerCase();
      if (value) {
        for (const row of wordListJson.keychains) {
          for (let i = maxLength + 1; i <= row.length; i++) {
            const chain = row.substring(i - maxLength - 1, i);
            if (value.includes(chain)) return { passwdKeyChain: chain };
          }
        }
      }
      return null;
    };
  }

  /**
   * Check if password contain equal characters repeated by self
   * @param {number} maxRepetition maximum length of repeating set
   * @return {ValidatorFn}
   */
  public static charRepeat(maxRepetition: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let counter = 1;
      const value: string | undefined = control.value;
      if (value) {
        for (let i = 1; i < value.length; i++) {
          if (value.charCodeAt(i) == value.charCodeAt(i - 1)) {
            if (++counter > maxRepetition) return { passwdCharRep: { char: value[i], length: counter } };
          } else {
            counter = 1;
          }
        }
      }
      return null;
    };
  }

  /**
   * Check if password contain sequential characters
   * @param {number} maxSeqLength maximum length of sequence
   * @return {ValidatorFn}
   */
  public static charSequence(maxSeqLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let counter = 1;
      const value: string | undefined = control.value;
      if (value) {
        for (let i = 1; i < value.length; i++) {
          if (Math.abs(value.charCodeAt(i) - value.charCodeAt(i - 1)) == 1) {
            if (++counter > maxSeqLength) {
              return { passwdCharSeq: { sequence: value.substring(i - maxSeqLength, i + 1), length: counter } };
            }
          } else {
            counter = 1;
          }
        }
      }
      return null;
    };
  }

  /**
   * Check if password contain small letter
   */
  public static smallLetter: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    return control.value?.match(/(?=.*\p{Ll}).*/u) ? null : { passwdChar: true, noSmallLetter: true };
  };

  /**
   * Check if password contain capital letter
   */
  public static bigLetter: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    return control.value?.match(/(?=.*\p{Lu}).*/u) ? null : { passwdChar: true, noBigLetter: true };
  };

  /**
   * Check if password contain digit
   */
  public static digit: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    return control.value?.match(/(?=.*[0-9]+).*/) ? null : { passwdChar: true, noDigit: true };
  };

  /**
   * Check if password contain one of ~`!@#$%^&*()-_+=|}]{["':;?/>.< characters
   */
  public static specialChar: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    return control.value?.match(/(?=.*[~`!@#$%^&*()\-_+=|}\]{["':;?/>.<]+).*/) ? null : { passwdChar: true, noSpecialChar: true };
  };

  /**
   * Aggregates all checks required by password safety policy into single validator function
   */
  public static policy: ValidatorFn = (control: AbstractControl): ValidationErrors => {
    return {
      ...Validators.minLength(this.MIN_LENGTH)(control),
      ...this.smallLetter(control),
      ...this.bigLetter(control),
      ...this.digit(control),
      ...this.specialChar(control)
      // ...this.charRepeat(this.MAX_REP)(control),
      // ...this.charSequence(this.MAX_SEQ)(control),
      // ...this.dictionary(control),
      // ...this.keyChain(this.MAX_KEY_CHAIN)(control)
    };
  };

  /**
   * Used for password confirmation, check if confirmation password is equal to first
   * @param {string} oldKey key of main password control in parent form group
   * @return {ValidatorFn}
   */
  public static confirm(passwdKey: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const password = control.parent?.get(passwdKey)?.value;
      const confirm = control.value;

      return password == confirm ? null : { passwdEqual: true };
    };
  }

  /**
   * Used for new Password, check if new password is different than old
   * @param {string} oldKey key of old password control in parent form group
   * @return {ValidatorFn}
   */
  public static new(oldKey: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const oldPassword = control.parent?.get(oldKey)?.value;
      const password = control.value;

      return oldPassword !== password ? null : { passwdPrev: true };
    };
  }
}
